import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Folder, FileText, ChevronRight, Loader2, X } from 'lucide-react';
import {
  sucheDateien, ladeOrdnerInhalt, formatiereGroesse, istGoogleFormat, ORDNER_MIME,
  type SuchTreffer, type DriveEintrag,
} from '../../lib/dokumente/aktenschrankApi';

export interface AktenschrankPickerProps {
  /** Aufruf mit der gewählten Datei; die aufrufende Seite kopiert sie. */
  onAuswahl: (datei: { id: string; name: string; mimeType: string }) => void;
  onClose: () => void;
  /** id der Datei, die gerade geholt wird (Spinner in der Zeile). */
  laufendeId?: string | null;
  /** Fehlertext der aufrufenden Seite (z. B. „zu groß"). */
  fehler?: string | null;
}

const GOOGLE_HINWEIS = 'Google-Dokumente können nicht angehängt werden';

/**
 * Eigenständiger, heller Aktenschrank-Picker (Google Drive) — Suche + Ordner-Browser mit
 * Pfad-Stapel, wie im dunklen Pendant in EmailVerfassen.tsx (~733–800), aber für helle
 * Oberflächen (z. B. Ticket-Detailseite) gebaut. Kopiert nichts selbst — die aufrufende
 * Seite lädt die Bytes (ladeDateiBlob) und legt den Anhang an; dieser Picker liefert nur die
 * Auswahl + optionale Lade-/Fehleranzeige.
 */
const AktenschrankPicker: React.FC<AktenschrankPickerProps> = ({ onAuswahl, onClose, laufendeId = null, fehler = null }) => {
  const [suche, setSuche] = useState('');
  const [treffer, setTreffer] = useState<SuchTreffer[]>([]);
  const [suchtLaedt, setSuchtLaedt] = useState(false);
  const suchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [stack, setStack] = useState<{ id: string; name: string }[]>([{ id: '', name: 'Aktenschrank' }]);
  const [inhalt, setInhalt] = useState<DriveEintrag[]>([]);
  const [browseLaedt, setBrowseLaedt] = useState(false);
  const initRef = useRef(false);

  const ladeOrdner = useCallback(async (id: string) => {
    setBrowseLaedt(true);
    try { setInhalt(await ladeOrdnerInhalt(id)); }
    catch { setInhalt([]); }
    finally { setBrowseLaedt(false); }
  }, []);

  // Beim Öffnen einmalig im Wurzelordner starten.
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    void ladeOrdner('');
  }, [ladeOrdner]);

  const handleSuche = (q: string) => {
    setSuche(q);
    if (suchTimer.current) clearTimeout(suchTimer.current);
    if (q.trim().length < 2) { setTreffer([]); setSuchtLaedt(false); return; }
    setSuchtLaedt(true);
    suchTimer.current = setTimeout(async () => {
      try { setTreffer(await sucheDateien(q.trim())); }
      catch { setTreffer([]); }
      finally { setSuchtLaedt(false); }
    }, 350);
  };

  useEffect(() => () => { if (suchTimer.current) clearTimeout(suchTimer.current); }, []);

  const oeffneUnterordner = (e: DriveEintrag) => {
    setStack((prev) => [...prev, { id: e.id, name: e.name }]);
    void ladeOrdner(e.id);
  };
  const springeZuBreadcrumb = (index: number) => {
    const neu = stack.slice(0, index + 1);
    setStack(neu);
    void ladeOrdner(neu[neu.length - 1].id);
  };

  const waehle = (id: string, name: string, mimeType: string) => {
    if (istGoogleFormat(mimeType) || laufendeId) return;
    onAuswahl({ id, name, mimeType });
  };

  const suchtAktiv = suche.trim().length >= 2;

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/40 backdrop-blur-sm lg:p-6" onClick={onClose}>
      <div
        className="flex h-full w-full flex-col overflow-hidden bg-white shadow-2xl lg:h-auto lg:max-h-[80vh] lg:w-full lg:max-w-md lg:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3 lg:px-5">
          <h2 className="text-sm font-black text-gray-900">Aus dem Aktenschrank</h2>
          <button
            type="button"
            onClick={onClose}
            className="tap-44 flex items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-700"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-4 pt-3 pb-2 lg:px-5">
          <div className="relative">
            <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" />
            <input
              type="text"
              value={suche}
              onChange={(e) => handleSuche(e.target.value)}
              placeholder="Suchen oder Ordner durchklicken…"
              className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-9 pr-3 text-[16px] text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-[#c4a77d] focus:bg-white"
            />
          </div>
        </div>

        {fehler && (
          <div className="mx-4 mb-2 rounded-lg bg-red-50 px-3 py-2 text-[12px] font-semibold text-red-600 lg:mx-5">
            {fehler}
          </div>
        )}

        {!suchtAktiv && (
          <div className="flex flex-wrap items-center gap-0.5 px-4 pb-2 text-[11px] text-gray-400 lg:px-5">
            {stack.map((s, i) => (
              <React.Fragment key={i}>
                {i > 0 && <ChevronRight size={12} className="opacity-60" />}
                <button
                  type="button"
                  onClick={() => springeZuBreadcrumb(i)}
                  className={`max-w-[140px] truncate hover:underline ${i === stack.length - 1 ? 'font-bold text-gray-900' : ''}`}
                >
                  {s.name}
                </button>
              </React.Fragment>
            ))}
          </div>
        )}

        <div className="max-h-[60vh] flex-1 overflow-y-auto px-2 pb-3 lg:px-3">
          {suchtAktiv ? (
            suchtLaedt ? (
              <div className="flex items-center gap-2 px-3 py-4 text-[13px] text-gray-400">
                <Loader2 size={15} className="animate-spin" /> Suche…
              </div>
            ) : treffer.length === 0 ? (
              <p className="px-3 py-4 text-[13px] text-gray-400">Keine Dokumente gefunden.</p>
            ) : (
              treffer.map((t) => {
                const google = istGoogleFormat(t.mimeType);
                const laedt = laufendeId === t.id;
                const meta = google ? GOOGLE_HINWEIS : [t.pfad, formatiereGroesse(t.size)].filter(Boolean).join(' · ');
                return (
                  <button
                    key={t.id}
                    type="button"
                    disabled={google || !!laufendeId}
                    onClick={() => waehle(t.id, t.name, t.mimeType)}
                    className="tap-44 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500">
                      {laedt ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[13px] font-semibold text-gray-900">{t.name}</p>
                      {meta && <p className="truncate text-[11px] text-gray-400">{meta}</p>}
                    </div>
                  </button>
                );
              })
            )
          ) : browseLaedt ? (
            <div className="flex items-center gap-2 px-3 py-4 text-[13px] text-gray-400">
              <Loader2 size={15} className="animate-spin" /> Lädt…
            </div>
          ) : inhalt.length === 0 ? (
            <p className="px-3 py-4 text-[13px] text-gray-400">Ordner ist leer.</p>
          ) : (
            inhalt.map((e) => {
              const istOrdner = e.mimeType === ORDNER_MIME;
              const google = !istOrdner && istGoogleFormat(e.mimeType);
              const laedt = laufendeId === e.id;
              const groesse = !istOrdner ? formatiereGroesse(e.size) : '';
              return (
                <button
                  key={e.id}
                  type="button"
                  disabled={google || !!laufendeId}
                  onClick={() => (istOrdner ? oeffneUnterordner(e) : waehle(e.id, e.name, e.mimeType))}
                  className="tap-44 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${istOrdner ? 'bg-[#c4a77d]/15 text-[#c4a77d]' : 'bg-gray-100 text-gray-500'}`}>
                    {laedt ? <Loader2 size={16} className="animate-spin" /> : istOrdner ? <Folder size={16} /> : <FileText size={16} />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13px] font-semibold text-gray-900">{e.name}</p>
                    {(groesse || google) && (
                      <p className="truncate text-[11px] text-gray-400">{google ? GOOGLE_HINWEIS : groesse}</p>
                    )}
                  </div>
                  {istOrdner && <ChevronRight size={15} className="shrink-0 text-gray-300" />}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default AktenschrankPicker;
